import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Cliente da edge function `simulador-site` (página pública /simule).
 *
 * A conversa vive no servidor; aqui só fica o token da sessão ("id.segredo")
 * e as mensagens para a tela. O formulário de início ("iniciar": nome, WhatsApp,
 * e-mail) vai com o token do Turnstile, abre a sessão e grava o lead; as
 * mensagens vão com o token da sessão.
 */

export type RespostaSimulador = {
  sessao?: string;
  resposta?: string;
  restantes?: number;
  contato?: boolean;
  nome?: string;
  ok?: boolean;
  erro?: string;
  campo?: string;
  expirada?: boolean;
  encerrada?: boolean;
};

export async function chamarSimulador(
  corpo: Record<string, unknown>,
  fetchImpl: typeof fetch = fetch,
): Promise<RespostaSimulador> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;
  try {
    const r = await fetchImpl(`${url}/functions/v1/simulador-site`, {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey: anon, Authorization: `Bearer ${anon}` },
      body: JSON.stringify(corpo),
    });
    const dados = (await r.json().catch(() => ({}))) as RespostaSimulador;
    if (!r.ok && !dados.erro) dados.erro = "Não foi possível responder agora. Tente novamente em instantes.";
    return dados;
  } catch {
    return { erro: "Sem conexão. Verifique a internet e tente novamente." };
  }
}

/**
 * Chave pública do Turnstile. Sem a variável, usa a chave de teste da
 * Cloudflare (sempre passa) — que o servidor de produção, com o segredo de
 * verdade, recusa. Ou seja: esquecer a variável fecha a página, não abre.
 */
export const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || "1x00000000000000000000AA";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opcoes: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

const SCRIPT_TURNSTILE = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function carregarTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  return new Promise((ok, falha) => {
    let s = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_TURNSTILE}"]`);
    if (!s) {
      s = document.createElement("script");
      s.src = SCRIPT_TURNSTILE;
      s.async = true;
      document.head.appendChild(s);
    }
    s.addEventListener("load", () => ok());
    s.addEventListener("error", () => falha(new Error("turnstile")));
  });
}

/** Widget do Turnstile em modo invisível (só aparece se a Cloudflare desconfiar). */
export function useTurnstile(ativo: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const idRef = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    if (!ativo || !ref.current) return;
    let vivo = true;
    carregarTurnstile()
      .then(() => {
        if (!vivo || !ref.current || !window.turnstile || idRef.current) return;
        idRef.current = window.turnstile.render(ref.current, {
          sitekey: TURNSTILE_SITE_KEY,
          appearance: "interaction-only",
          theme: "dark",
          language: "pt-br",
          callback: (t: string) => {
            setToken(t);
            setFalhou(false);
          },
          "expired-callback": () => setToken(null),
          "error-callback": () => setFalhou(true),
        });
      })
      .catch(() => {
        if (vivo) setFalhou(true);
      });
    return () => {
      vivo = false;
    };
  }, [ativo]);

  /** Token é de uso único: depois de gastar, pede outro. */
  const renovar = useCallback(() => {
    setToken(null);
    if (window.turnstile && idRef.current) window.turnstile.reset(idRef.current);
  }, []);

  return { ref, token, falhou, renovar };
}

/** "**R$ 1.000,00**" → partes para negrito, sem HTML vindo do servidor. */
export function partesComNegrito(texto: string): { texto: string; negrito: boolean }[] {
  return texto
    .split(/(\*\*[^*]+\*\*)/g)
    .filter(Boolean)
    .map((p) => (p.startsWith("**") && p.endsWith("**") && p.length > 4
      ? { texto: p.slice(2, -2), negrito: true }
      : { texto: p.replace(/\*\*/g, ""), negrito: false }));
}
